import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  TrendingUp,
  People,
  SmartToy,
  Assessment,
  Warning,
  CheckCircle,
  Error,
  Schedule,
  Refresh,
  Notifications,
  Storage,
  Speed,
  Timeline,
  CloudQueue,
  Functions,
  Api,
  NetworkCheck,
  CachedOutlined,
  FlashOn,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useApi } from '../../contexts/ApiContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';

interface SystemMetrics {
  users: {
    total: number;
    active: number;
    newToday: number;
    newThisWeek: number;
  };
  bots: {
    total: number;
    active: number;
    trading: number;
    paused: number;
    stopped: number;
    error: number;
  };
  trades: {
    today: number;
    thisWeek: number;
    thisMonth: number;
    successRate: number;
    totalVolume: number;
    totalFees: number;
  };
  signals: {
    today: number;
    processed: number;
    matched: number;
    pending: number;
    confidence: number;
  };
  system: {
    health: 'HEALTHY' | 'WARNING' | 'CRITICAL';
    apiSuccessRate: number;
    errorRate: number;
    responseTime: number;
    lastHealthCheck: string;
    // Serverless infrastructure metrics
    lambdaInvocations: number;
    lambdaErrors: number;
    lambdaDuration: number;
    apiGatewayRequests: number;
    dynamodbThrottles: number;
    cacheMissRate: number;
    websocketConnections: number;
  };
  queues: {
    trading: number;
    signals: number;
    notifications: number;
    exports: number;
  };
  alerts: Array<{
    id: string;
    type: 'error' | 'warning' | 'info';
    message: string;
    timestamp: string;
    acknowledged: boolean;
  }>;
}

interface PerformanceData {
  timestamp: string;
  responseTime: number;
  errorRate: number;
  activeUsers: number;
  tradesPerHour: number;
  // Serverless performance metrics
  lambdaInvocations: number;
  lambdaDuration: number;
  apiGatewayRequests: number;
  dynamodbReadCapacity: number;
  dynamodbWriteCapacity: number;
  cacheMissRate: number;
}

interface RecentActivity {
  id: string;
  type: 'user_registration' | 'bot_created' | 'trade_executed' | 'admin_action';
  description: string;
  timestamp: string;
  userId?: string;
  metadata?: any;
}

const Dashboard: React.FC = () => {
  const api = useApi();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const { data: overview, isLoading, refetch: refetchOverview } = useQuery({
    queryKey: ['systemOverview'],
    queryFn: () => api.get('/system/overview'),
    refetchInterval: autoRefresh ? 60000 : false, // Refresh every minute
    onSuccess: () => setLastRefresh(new Date()),
    retry: false, // Don't retry failed requests
    onError: (error) => {
      console.warn('Failed to fetch system overview:', error);
    },
  });

  useQuery({
    queryKey: ['healthCheck'],
    queryFn: () => api.get('/system/health'),
    refetchInterval: autoRefresh ? 60000 : false, // Refresh every minute
    retry: false,
    onError: (error) => {
      console.warn('Failed to fetch health check:', error);
    },
  });

  const { data: performanceData } = useQuery({
    queryKey: ['performanceData'],
    queryFn: () => api.get('/system/performance?hours=24'),
    refetchInterval: autoRefresh ? 300000 : false, // Refresh every 5 minutes
    retry: false,
    onError: (error) => {
      console.warn('Failed to fetch performance data:', error);
    },
  });

  const { data: recentActivity } = useQuery({
    queryKey: ['recentActivity'],
    queryFn: () => api.get('/system/activity?limit=10'),
    refetchInterval: autoRefresh ? 60000 : false, // Refresh every minute
    retry: false,
    onError: (error) => {
      console.warn('Failed to fetch recent activity:', error);
    },
  });

  const { data: systemStats } = useQuery({
    queryKey: ['systemStats'],
    queryFn: () => api.get('/system/statistics'),
    refetchInterval: autoRefresh ? 120000 : false, // Refresh every 2 minutes
    retry: false,
    onError: (error) => {
      console.warn('Failed to fetch system stats:', error);
    },
  });

  // Manual refresh function
  const handleManualRefresh = () => {
    refetchOverview();
    setLastRefresh(new Date());
  };

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  if (isLoading) return <LoadingSpinner />;

  // Use real data only - no fallback mock data
  console.log('overview: ', overview);
  const metrics = overview?.overview;

  // Show a warning if we don't have real data
  const isUsingFallbackData = !overview;

  const getHealthColor = (health?: string) => {
    switch (health) {
      case 'HEALTHY': return 'success';
      case 'WARNING': return 'warning';
      case 'CRITICAL': return 'error';
      default: return 'warning';
    }
  };

  const getHealthIcon = (health?: string) => {
    switch (health) {
      case 'HEALTHY': return <CheckCircle color="success" />;
      case 'WARNING': return <Warning color="warning" />;
      case 'CRITICAL': return <Error color="error" />;
      default: return <Warning color="warning" />;
    }
  };

  const formatLastHealthCheck = (timestamp: string) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Enhanced Header with Controls */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mb: 4,
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
            System Overview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule fontSize="small" />
            {isUsingFallbackData
              ? 'Using demo data (API unavailable)'
              : `Last updated: ${lastRefresh.toLocaleTimeString()}`
            }
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Tooltip title={autoRefresh ? "Disable auto-refresh" : "Enable auto-refresh"}>
            <IconButton
              onClick={toggleAutoRefresh}
              color={autoRefresh ? "primary" : "default"}
              sx={{
                bgcolor: autoRefresh ? 'primary.50' : 'grey.100',
                '&:hover': { bgcolor: autoRefresh ? 'primary.100' : 'grey.200' }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleManualRefresh}
            size="medium"
            sx={{ borderRadius: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Assessment />}
            size="medium"
            href="/export"
            sx={{ borderRadius: 2, boxShadow: 2 }}
          >
            Export Data
          </Button>
        </Box>
      </Box>

      {/* API Unavailable Warning */}
      {isUsingFallbackData && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <AlertTitle>API Unavailable</AlertTitle>
            Some API endpoints are not available. Displaying demo data for development purposes.
          </Alert>
        </Box>
      )}

      {/* System Alerts Banner */}
      {overview?.alerts && overview.alerts.filter((alert: any) => !alert.acknowledged).length > 0 && (
        <Box sx={{ mb: 3 }}>
          {overview.alerts
            .filter((alert: any) => !alert.acknowledged)
            .slice(0, 3)
            .map((alert: any) => (
              <Alert
                key={alert.id}
                severity={alert.type}
                sx={{ mb: 1 }}
                action={
                  <Button size="small" onClick={() => {/* TODO: Acknowledge alert */}}>
                    Acknowledge
                  </Button>
                }
              >
                <AlertTitle>{alert.type.toUpperCase()}</AlertTitle>
                {alert.message}
              </Alert>
            ))}
        </Box>
      )}

      {/* Enhanced System Health Status */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  {getHealthIcon(metrics?.system?.health)}
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    System Health
                  </Typography>
                </Box>
                <Chip
                  label={metrics?.system?.health ?? 'UNKNOWN'}
                  color={getHealthColor(metrics?.system?.health) as any}
                  variant="filled"
                  sx={{
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'capitalize'
                  }}
                />
              </Box>

              {/* System Metrics Grid */}
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Box
                    textAlign="center"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                      API Success Rate
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {metrics?.system?.apiSuccessRate?.toFixed(1) ?? '?'}%
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box
                    textAlign="center"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                      Last Health Check
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                      {metrics?.system?.lastHealthCheck ? formatLastHealthCheck(metrics.system.lastHealthCheck) : '?'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box
                    textAlign="center"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                      Avg Response Time
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                      {metrics?.system?.responseTime ?? '?'}ms
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box
                    textAlign="center"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                      Lambda Invocations
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {metrics?.system?.lambdaInvocations?.toLocaleString() ?? '?'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Performance Metrics */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Serverless Infrastructure
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">
                          Lambda Duration
                        </Typography>
                        <Typography variant="body2">
                          {metrics?.system?.lambdaDuration ?? '?'}ms
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(((metrics?.system?.lambdaDuration || 0) / 15000) * 100, 100)}
                        color={
                          (metrics?.system?.lambdaDuration || 0) > 10000 ? "error" :
                          (metrics?.system?.lambdaDuration || 0) > 5000 ? "warning" : "primary"
                        }
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">
                          Cache Miss Rate
                        </Typography>
                        <Typography variant="body2">
                          {metrics?.system?.cacheMissRate ?? '?'}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={metrics?.system?.cacheMissRate || 0}
                        color={
                          (metrics?.system?.cacheMissRate || 0) > 20 ? "error" :
                          (metrics?.system?.cacheMissRate || 0) > 10 ? "warning" : "primary"
                        }
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">
                          DynamoDB Throttles
                        </Typography>
                        <Typography variant="body2">
                          {metrics?.system?.dynamodbThrottles ?? '?'}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(((metrics?.system?.dynamodbThrottles || 0) / 100) * 100, 100)}
                        color={
                          (metrics?.system?.dynamodbThrottles || 0) > 50 ? "error" :
                          (metrics?.system?.dynamodbThrottles || 0) > 20 ? "warning" : "primary"
                        }
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                Service Health
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Api color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Core Services API"
                    secondary={`${metrics?.system?.apiSuccessRate?.toFixed(1) ?? '?'}% success rate`}
                  />
                  <CheckCircle color="success" fontSize="small" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUp color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Trading Services"
                    secondary="All exchanges connected"
                  />
                  <CheckCircle color="success" fontSize="small" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Storage color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Database"
                    secondary="DynamoDB operational"
                  />
                  <CheckCircle color="success" fontSize="small" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CloudQueue color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Background Jobs"
                    secondary="All queues processing"
                  />
                  <CheckCircle color="success" fontSize="small" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <NetworkCheck color={isUsingFallbackData ? "warning" : "success"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Admin Dashboard"
                    secondary={isUsingFallbackData ? "API endpoints unavailable" : "Fully operational"}
                  />
                  {isUsingFallbackData ? (
                    <Warning color="warning" fontSize="small" />
                  ) : (
                    <CheckCircle color="success" fontSize="small" />
                  )}
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enhanced Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            borderRadius: 3,
            boxShadow: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <People color="primary" sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Total Users
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {metrics?.users?.total?.toLocaleString() ?? '?'}
                    </Typography>
                  </Box>
                </Box>
                <Badge
                  badgeContent={metrics?.users?.newToday ?? '?'}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: 1
                    }
                  }}
                >
                  <Box />
                </Badge>
              </Box>
              <Box sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {metrics?.users?.active ?? '?'} active • {metrics?.users?.newToday ?? '?'} new today
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {metrics?.users?.newThisWeek ?? '?'} new this week
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            borderRadius: 3,
            boxShadow: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'secondary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <SmartToy color="secondary" sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Trading Bots
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {metrics?.bots?.total?.toLocaleString() ?? '?'}
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Chip
                    label={`${metrics?.bots?.trading ?? '?'} trading`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600, borderRadius: 1 }}
                  />
                </Box>
              </Box>
              <Box sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {metrics?.bots?.active ?? '?'} active • {metrics?.bots?.paused ?? '?'} paused
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {metrics?.bots?.stopped ?? '?'} stopped • {metrics?.bots?.error ?? '?'} error
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            borderRadius: 3,
            boxShadow: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <TrendingUp color="success" sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Trades Today
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {metrics?.trades?.today?.toLocaleString() ?? '?'}
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Chip
                    label={`${metrics?.trades?.successRate?.toFixed(1) ?? '?'}%`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600, borderRadius: 1 }}
                  />
                </Box>
              </Box>
              <Box sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  ${metrics?.trades?.totalVolume?.toLocaleString() ?? '?'} volume
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  ${metrics?.trades?.totalFees?.toLocaleString() ?? '?'} fees
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            borderRadius: 3,
            boxShadow: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'info.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Assessment color="info" sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Signals Today
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {metrics?.signals?.today?.toLocaleString() ?? '?'}
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Chip
                    label={`${metrics?.signals?.confidence?.toFixed(1) ?? '?'}% avg`}
                    color="info"
                    size="small"
                    sx={{ fontWeight: 600, borderRadius: 1 }}
                  />
                </Box>
              </Box>
              <Box sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {metrics?.signals?.processed ?? '?'} processed • {metrics?.signals?.matched ?? '?'} matched
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {metrics?.signals?.pending ?? '?'} pending
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Performance (24 Hours)
              </Typography>
              {performanceData && performanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={(value) => new Date(value).toLocaleTimeString()}
                    />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <RechartsTooltip
                      labelFormatter={(value) => new Date(value).toLocaleString()}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="responseTime"
                      stroke="#8884d8"
                      name="Response Time (ms)"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="errorRate"
                      stroke="#82ca9d"
                      name="Error Rate"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="lambdaInvocations"
                      stroke="#ffc658"
                      name="Lambda Invocations"
                    />
                    {performanceData && performanceData[0]?.lambdaDuration !== undefined && (
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="lambdaDuration"
                        stroke="#ff7300"
                        name="Lambda Duration (ms)"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="text.secondary" variant="h6" gutterBottom>
                    No Performance Data Available
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {isUsingFallbackData
                      ? 'Performance metrics will appear when API endpoints are available'
                      : 'Performance data will appear once the system starts collecting metrics'
                    }
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Queue Status
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUp color={(metrics?.queues?.trading || 0) > 100 ? "warning" : "success"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Trading Queue"
                    secondary={`${metrics?.queues?.trading || 0} pending jobs`}
                  />
                  <Chip
                    label={metrics?.queues?.trading || 0}
                    size="small"
                    color={(metrics?.queues?.trading || 0) > 100 ? "warning" : "default"}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <Assessment color={(metrics?.queues?.signals || 0) > 50 ? "warning" : "success"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Signals Queue"
                    secondary={`${metrics?.queues?.signals || 0} pending jobs`}
                  />
                  <Chip
                    label={metrics?.queues?.signals || 0}
                    size="small"
                    color={(metrics?.queues?.signals || 0) > 50 ? "warning" : "default"}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <Notifications color={(metrics?.queues?.notifications || 0) > 200 ? "warning" : "success"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Notifications Queue"
                    secondary={`${metrics?.queues?.notifications || 0} pending jobs`}
                  />
                  <Chip
                    label={metrics?.queues?.notifications || 0}
                    size="small"
                    color={(metrics?.queues?.notifications || 0) > 200 ? "warning" : "default"}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <Storage color={(metrics?.queues?.exports || 0) > 10 ? "warning" : "success"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Export Queue"
                    secondary={`${metrics?.queues?.exports || 0} pending jobs`}
                  />
                  <Chip
                    label={metrics?.queues?.exports || 0}
                    size="small"
                    color={(metrics?.queues?.exports || 0) > 10 ? "warning" : "default"}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity and Analytics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              {recentActivity && recentActivity.length > 0 ? (
                <List dense>
                  {recentActivity.slice(0, 8).map((activity: RecentActivity, index: number) => (
                    <React.Fragment key={activity.id}>
                      <ListItem>
                        <ListItemIcon>
                          {activity.type === 'user_registration' && <People color="primary" />}
                          {activity.type === 'bot_created' && <SmartToy color="secondary" />}
                          {activity.type === 'trade_executed' && <TrendingUp color="success" />}
                          {activity.type === 'admin_action' && <Assessment color="info" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.description}
                          secondary={new Date(activity.timestamp).toLocaleString()}
                        />
                      </ListItem>
                      {index < recentActivity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" variant="h6" gutterBottom>
                    No Recent Activity
                  </Typography>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    {isUsingFallbackData
                      ? 'Activity feed will appear when API endpoints are available'
                      : 'Recent system activity will appear here as events occur'
                    }
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Trading Performance
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Success Rate
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={metrics?.trades?.successRate ?? 0}
                  color={(metrics?.trades?.successRate ?? 0) > 80 ? "success" : (metrics?.trades?.successRate ?? 0) > 60 ? "warning" : "error"}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {metrics?.trades?.successRate?.toFixed(1) ?? '?'}% success rate
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="h6">{metrics?.trades?.today ?? '?'}</Typography>
                    <Typography variant="body2" color="text.secondary">Today</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="h6">{metrics?.trades?.thisWeek ?? '?'}</Typography>
                    <Typography variant="body2" color="text.secondary">This Week</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="h6">{metrics?.trades?.thisMonth ?? '?'}</Typography>
                    <Typography variant="body2" color="text.secondary">This Month</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="h6">${metrics?.trades?.totalVolume?.toLocaleString() ?? '?'}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Volume</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Analytics Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                User Activity Distribution
              </Typography>
              {systemStats?.userActivity ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={systemStats?.userActivity || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {(systemStats?.userActivity || []).map((_: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={250}>
                  <Typography color="text.secondary" variant="h6" gutterBottom>
                    No User Activity Data
                  </Typography>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    {isUsingFallbackData
                      ? 'User activity charts will appear when API endpoints are available'
                      : 'User activity distribution will appear once data is collected'
                    }
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Bot Status Distribution
              </Typography>
              {systemStats?.botStatus ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={systemStats?.botStatus || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="status" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={250}>
                  <Typography color="text.secondary" variant="h6" gutterBottom>
                    No Bot Status Data
                  </Typography>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    {isUsingFallbackData
                      ? 'Bot status charts will appear when API endpoints are available'
                      : 'Bot status distribution will appear once bots are created'
                    }
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
